import { HttpClient } from "@angular/common/http";
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { HasId } from './has-id.interface';

export class BasicDataSubService<T extends HasId> {
	private lastRead: Date | null = null;
	
	// NB: la commande de reset de cache est optionnelle, car cette classe est aussi utilisee pour
	// la lecture des donnees statiques, qui n'expirent jamais
	constructor(
		protected httpClient: HttpClient,
		protected url: string,
		clearCacheCommand?: Observable<Date>
	) {
		if (clearCacheCommand) {
			clearCacheCommand.subscribe(
				(lastUpdate) => {
					if (this.lastRead === null){
						this.clearCaches();
						this.lastRead = lastUpdate;
					} else {
						// faut ramener lastRead à UTC car lastUpdate est en UTC
						let lastReadUtc = new Date(this.lastRead).setMinutes(this.lastRead.getMinutes() + this.lastRead.getTimezoneOffset());
						if (lastUpdate.getTime() > new Date(lastReadUtc).getTime()) {
							this.clearCaches();
							this.lastRead = lastUpdate;
						}
					}
				}
			);
		}
	}
	
	public clearCaches(): void {
		this.listCache = null;
		this.mapByIdCache = null;
		this.mapByForeignKeyCache = new Map<string, Observable<Map<number, T[]>>>();
		this.lastRead = null;
	}

	private listCache: Observable<T[]> | null = null;
	public getList(): Observable<T[]> {
		if (!this.listCache) {
			this.listCache = this.httpClient //
				.get<T[]>(this.url, { observe: 'response' }) //
				.pipe(
					map(
						(response) => {
							let headers = response.headers;
							let date = headers.get("Date");
							if (date) {
	 							let dd = new Date(date);
								this.lastRead = dd;
							}
							
							let collection = response.body;
							return collection ? collection : [];
						}
					),
					shareReplay(1)
				) //
				;
		}

		return this.listCache;
	}

	private mapByIdCache: Observable<Map<number, T>> | null = null;
	public getMapById(): Observable<Map<number, T>> {
		if (!this.mapByIdCache) {
			this.mapByIdCache = this.getList() //
				.pipe(
					map(
						(list: T[]) => {
							let map = new Map<number, T>();
							for (let i = 0; i < list.length; i++) {
								const element = list[i];
								map.set(element.id, element);
							}
							return map;
						}
					),
					shareReplay(1)
				) //
				;
		}
		return this.mapByIdCache;
	}
	
	public getById(id: number): Observable<T | undefined> {
		return this.getMapById().pipe(
			map(
				(map: Map<number, T>) => map.get(id)
			)
		);
	}
	
	public getKnownById(id: number, typeName: string): Observable<T> {
		return this.getMapById().pipe(
			map(
				(map: Map<number, T>) => {
					const existant = map.get(id);
					if (existant) {
						return existant;
					} else {
						throw new Error("Unkown " + typeName + " id: "+ id);
					}
				}
			)
		);
	}

	private mapByForeignKeyCache = new Map<string, Observable<Map<number, T[]>>>();
	public getMapByForeignKey(cacheKey: string, keyGetter: (entity: T) => number | null): Observable<Map<number, T[]>> {
		let cache = this.mapByForeignKeyCache.get(cacheKey);
		if (!cache) {
			cache = this.getList() //
				.pipe(
					map(
						(list: T[]) => {
							let map = new Map<number, T[]>();
							for (let i = 0; i < list.length; i++) {
								const element = list[i];
								const foreignKey = keyGetter(element);
								if (foreignKey != null) {
									let siblings = map.get(foreignKey);
									if (!siblings) {
										siblings = [];
										map.set(foreignKey, siblings);
									}
									siblings.push(element);
								}
							}
							return map;
						}
					)
				)
				;
			this.mapByForeignKeyCache.set(cacheKey, cache);
		}
		return cache;
	}
	
	public getByForeignKey(cacheKey: string, keyGetter: (entity: T) => number | null, key: number): Observable<T[]> {
		return this.getMapByForeignKey(cacheKey, keyGetter).pipe(
			map(
				(map: Map<number, T[]>) => {
					const list = map.get(key);
					return list ? list : [];
				}
			)
		);
	}
}